import { GroupedField, type GroupedFieldProps } from "@ngt-form/mantine";
import type { ComponentType } from "react";

import AppSettings from "src/AppSettings";
import { fieldTourTargetProps } from "src/help/helpTourTargets";
import type { TMDValidationError } from "src/types/TMDValidationError";

/**
 * Applies the configured long label/field spans to the shared grouped field.
 * Postconditions: Explicit nonnull spans, including zero, are retained. Other column options and
 * field props are preserved; only missing spans fall back to application defaults.
 * Assumptions: Form context supplies metadata; the existing type-suppression boundary is unchanged.
 */
const LongGroupedField = <
    TComponent extends ComponentType<any>,
    TFormValue extends object = object,
    TFormMetadata extends object = object,
    TFieldValue = unknown,
    TLookupItem = unknown
>({
    groupedFieldOptions,
    ...props
}: GroupedFieldProps<
    TComponent,
    TFormValue,
    TMDValidationError,
    TFormMetadata,
    TFieldValue,
    TLookupItem
>) => {
    return (
        // @ts-ignore
        <GroupedField
            {...props}
            groupedFieldOptions={{
                ...groupedFieldOptions,
                // Mark the existing root as the field's tour stop; a wrapper would break row striping.
                boxProps: {
                    ...groupedFieldOptions?.boxProps,
                    ...fieldTourTargetProps(props.propertyPath)
                },
                labelColumnProps: {
                    ...groupedFieldOptions?.labelColumnProps,
                    span:
                        groupedFieldOptions?.labelColumnProps?.span ??
                        AppSettings.forms.groupedFields.longFieldColumnSpans.label
                },
                fieldColumnProps: {
                    ...groupedFieldOptions?.fieldColumnProps,
                    span:
                        groupedFieldOptions?.fieldColumnProps?.span ??
                        AppSettings.forms.groupedFields.longFieldColumnSpans.field
                }
            }}
        />
    );
};

export default LongGroupedField;
